<template>
  <div class="code-editor" :style="{ height }">
    <ClientOnly>
      <MonacoEditor
        v-model="model"
        :lang="lang"
        :options="mergedOptions"
        style="width:100%; height:100%;"
        @load="onLoad"
      />
      <template #fallback>
        <div style="padding:12px; color:var(--muted);">Loading the editor…</div>
      </template>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    modelValue: string
    lang?: string
    height?: string
  }>(),
  { lang: 'html', height: '280px' },
)
const emit = defineEmits<{ 'update:modelValue': [string] }>()

const model = computed({
  get: () => props.modelValue,
  set: (v: string) => emit('update:modelValue', v),
})

let editor: any = null
function onLoad(ed: any) {
  editor = ed
}

const mergedOptions = {
  theme: 'vs-dark',
  automaticLayout: true,
  lineNumbers: 'off' as const,
  minimap: { enabled: false },
  fontSize: 12,
  fontFamily: 'var(--font-mono), monospace',
  scrollBeyondLastLine: false,
  wordWrap: 'on' as const,
  tabSize: 2,
  renderLineHighlight: 'none' as const,
  padding: { top: 8, bottom: 8 },
  scrollbar: { verticalScrollbarSize: 8, horizontalScrollbarSize: 8 },
}

/** Insert text at the cursor (used by the Outlook snippet helpers). */
function insertText(text: string) {
  if (!editor) {
    emit('update:modelValue', (props.modelValue || '') + text)
    return
  }
  const sel = editor.getSelection()
  editor.executeEdits('snippet', [{ range: sel, text, forceMoveMarkers: true }])
  editor.focus()
}

/** Run Monaco's HTML formatter. */
async function format() {
  try {
    await editor?.getAction?.('editor.action.formatDocument')?.run?.()
  } catch {
    // formatter not ready
  }
}

defineExpose({ insertText, format })
</script>

<style scoped>
.code-editor {
  border: 1px solid var(--line2);
  border-radius: 8px;
  overflow: hidden;
  background: #1e1e1e;
}
</style>
